import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Uses Intersection Observer to detect when an element is in view inside a scroll container.
 * Use so statements/transactions panes don't fetch until they're visible (e.g. not below the fold).
 *
 * When rootRef is provided we ONLY use that as root (never viewport).
 *
 * @param {{ rootRef?: React.RefObject<Element>, rootReady?: boolean, rootMargin?: string, threshold?: number }} [options]
 *   rootRef: optional scroll container. When provided, we never use viewport - only observe when root is set.
 *   rootReady: set to true when rootRef.current is available (e.g. after scroll container mounts).
 *   rootMargin: optional margin around root (default: '0px')
 *   threshold: optional ratio of visibility (0-1, default: 0.01 = 1% visible so a sliver doesn't count)
 * @returns {[ (el: Element | null) => void, boolean ]} [refCallback, isInView]
 */
export function useInView(options = {}) {
    const { rootRef, rootReady = true, rootMargin = '0px', threshold = 0.01 } = options;
    const [element, setElement] = useState(null);
    const [isInView, setIsInView] = useState(false);
    const rootReadyOrNone = rootRef ? rootReady : true;
    const observerRef = useRef(null);

    const refCallback = useCallback((el) => {
        setElement(el);
    }, []);

    useEffect(() => {
        if (!element) {
            setIsInView(false);
            return undefined;
        }
        // When rootRef is provided, wait until rootReady so we don't use viewport by mistake
        if (rootRef && !rootReadyOrNone) {
            setIsInView(false);
            return undefined;
        }
        const root = rootRef?.current ?? null;
        // When we were given a rootRef, never use viewport (root null) - wait until root is available
        if (rootRef && !root) {
            setIsInView(false);
            return undefined;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInView(entry.isIntersecting);
            },
            { root, rootMargin, threshold }
        );
        observerRef.current = observer;
        observer.observe(element);
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [element, rootRef, rootReadyOrNone, rootMargin, threshold]);

    return [refCallback, isInView];
}
